import { put, select, takeLatest, all } from 'redux-saga/effects';
import { notify } from '@mrlannigan/reapop';
import { sagaError } from 'helpers/handleGenericSagaError';
import { push } from 'connected-react-router';
import { get } from 'lodash';
import apiAuthCall from 'helpers/apiAuthCall';
import { throttle } from 'helpers/sagaEffects';
import {
    fetchListSuccess,
    actions,
    createBrandForm,
    fetchBrandSuccess,
    updateBrandForm,
    updateBrandBrandingForm,
    updateBrandOptionsForm,
    fetchBrand as fetchBrandAction,
    deleteBrandSuccess
} from './duck';
import {
    brandsEndpoint,
    updateBrandEndpoint,
    updateBrandBrandingEndpoint,
    updateBrandOptionsEndpoint,
    getBrandEndpoint
} from './selectors';

export function* createBrand(api, action) {
    const notificationId = 'createBrand';

    try {
        const payload = action.payload;

        const result = yield apiAuthCall(
            action.getState(),
            [api, api.request],
            {
                method: 'post',
                url: yield select(brandsEndpoint),
                data: payload
            }
        );

        yield put(createBrandForm.success());
        yield put(notify({
            id: notificationId,
            message: `${action.payload.name || 'Brand'} created!`,
            status: result.status
        }));
        yield put(push(`/admin/brands/${result.data.id}`));
    } catch (err) {
        yield put(createBrandForm.failure(err));
        yield put(notify({
            id: notificationId,
            ...sagaError(err)
        }));
    }
}

export function* fetchBrandList(api, action) {
    const notificationId = 'getBrandList';

    try {
        const result = yield apiAuthCall(
            action.getState(),
            [api, api.request],
            {
                method: 'get',
                url: yield select(brandsEndpoint)
            }
        );

        yield put(fetchListSuccess(result.data));
    } catch (err) {
        yield put(notify({
            id: notificationId,
            ...sagaError(err)
        }));
    }
}

export function* fetchBrand(api, action) {
    const notificationId = action.type;

    try {
        const result = yield apiAuthCall(
            action.getState(),
            [api, api.request],
            {
                method: 'get',
                url: yield select(getBrandEndpoint, action.payload)
            }
        );

        yield put(fetchBrandSuccess(result.data));
    } catch (err) {
        yield put(notify({
            id: notificationId,
            ...sagaError(err)
        }));
    }
}

export function* updateBrand(api, action) {
    const notificationId = 'updateBrand';
    try {
        const result = yield apiAuthCall(
            action.getState(),
            [api, api.request],
            {
                method: 'put',
                url: yield select(updateBrandEndpoint, { brandId: action.payload.id }),
                data: action.payload
            }
        );

        yield put(updateBrandForm.success());
        yield put(notify({
            id: notificationId,
            message: `${action.payload.name || 'Brand'} updated!`,
            status: result.status
        }));
        yield put(push('/admin/brands'));
    } catch (err) {
        yield put(updateBrandForm.failure(err));
        yield put(notify({
            id: notificationId,
            ...sagaError(err)
        }));
    }
}

export function* updateBrandBranding(api, action) {
    const notificationId = 'updateBrandBranding';
    try {
        const formData = new FormData();
        const image = get(action, 'payload.branding.image[0]');
        if (image) {
            formData.append('image', image);
            formData.append('imageName', image.name);
        }

        [
            'headerBarColor',
            'sideBarColor',
            'highlightColor',
            'buttonColor',
            'removeLogo',
        ].forEach((key) => {
            if ([undefined, null, ''].indexOf(action.payload.branding[key]) === -1) {
                formData.append(key, action.payload.branding[key]);
            }
        });

        const result = yield apiAuthCall(
            action.getState(),
            [api, api.request],
            {
                method: 'put',
                headers: { 'content-type': 'multipart/form-data' },
                url: yield select(updateBrandBrandingEndpoint, { brandId: action.payload.id }),
                data: formData
            }
        );

        yield put(fetchBrandAction({ brandId: action.payload.id }));
        yield put(updateBrandBrandingForm.success());
        yield put(notify({
            id: notificationId,
            message: `${action.payload.name} branding updated successfully`,
            status: result.status
        }));
    } catch (err) {
        yield put(updateBrandBrandingForm.failure(err));
        yield put(notify({
            id: notificationId,
            ...sagaError(err)
        }));
    }
}

export function* deleteBrand(api, confirmMsg, action) {
    const notificationId = 'deleteBrand';

    if (!confirmMsg('Are you sure you wish to delete this brand?')) { // eslint-disable-line no-alert
        return;
    }

    try {
        const result = yield apiAuthCall(
            action.getState(),
            [api, api.request],
            {
                method: 'delete',
                url: yield select(updateBrandEndpoint, { brandId: action.payload.id })
            }
        );

        yield put(notify({
            id: notificationId,
            message: `${action.payload.name || 'Brand'} deleted!`,
            status: result.status
        }));
        yield put(deleteBrandSuccess());
        yield put(push('/admin/brands'));
    } catch (err) {
        yield put(notify({
            id: notificationId,
            ...sagaError(err)
        }));
    }
}

export function* updateBrandOptions(api, action) {
    const notificationId = 'updateBrandOptions';

    try {
        const result = yield apiAuthCall(
            action.getState(),
            [api, api.request],
            {
                method: 'put',
                url: yield select(updateBrandOptionsEndpoint, { brandId: action.payload.id }),
                data: action.payload.meta
            }
        );

        yield put(fetchBrandAction({ brandId: action.payload.id }));
        yield put(updateBrandOptionsForm.success());
        yield put(notify({
            id: notificationId,
            message: `${action.payload.name} options updated successfully`,
            status: result.status
        }));
    } catch (err) {
        yield put(updateBrandOptionsForm.failure(err));
        yield put(notify({
            id: notificationId,
            ...sagaError(err)
        }));
    }
}


export default function* brandSaga({ api }) {
    yield all([
        takeLatest(createBrandForm.REQUEST, createBrand, api),
        takeLatest(updateBrandForm.REQUEST, updateBrand, api),
        takeLatest(updateBrandBrandingForm.REQUEST, updateBrandBranding, api),
        takeLatest(updateBrandOptionsForm.REQUEST, updateBrandOptions, api),
        throttle(200, actions.FETCH_BRAND_LIST, fetchBrandList, api),
        takeLatest(actions.FETCH_BRAND, fetchBrand, api),
        takeLatest(actions.DELETE_BRAND, deleteBrand, api, window.confirm),
    ]);
}



// WEBPACK FOOTER //
// ./src/services/brands/saga.js